import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import { formatCurrency, handleCurrencyInputChange, parseCurrency } from '../../../app/currency';
import { ProductSearchAutocomplete } from '../../products/public';
import { getSKUs } from '../../../app/api';
import LineItemCatalogAttributesSection from './LineItemCatalogAttributesSection';
import {
  attributeOptionStrings,
  loadProductCatalogAttributes,
  registerLineItemAsNewProduct,
  registerLineItemOnExistingProduct,
  SKU_LINK_CREATE_NEW,
  SKU_LINK_EXISTING,
  SKU_LINK_REUSE_MATCH,
  truncateProductName,
} from './lineItemCatalogConversion';
import {
  catalogAttributesForSku,
  catalogAttributesRequiredForServerSku,
  createDefaultVariationDraftFromItem,
  mergeCatalogWithDraftKeys,
} from './lineItemCatalogAttributeUtils';
import {
  OptionDefaultsForExistingDialog,
  detectNewDraftAttributes,
  draftAttributesNeedExistingDefaults,
} from '../../core/public';
import RegisterLineItemCatalogMediaSection from './RegisterLineItemCatalogMediaSection';
import { finalizeRegisterLineItemCatalogMedias } from './lineItemCatalogMediaImport';

const PRODUCT_TYPES = [
  { value: 'PRODUCT', labelKey: 'products.item_type_product' },
  { value: 'SERVICE', labelKey: 'products.item_type_service' },
  { value: 'SUPPLY', labelKey: 'products.item_type_supply' },
];

export default function RegisterLineItemAsProductDialog({
  open,
  formItem,
  initialDescription = '',
  orderId,
  order,
  formItems,
  onClose,
  onEnsureItemSaved,
  onConverted,
  onError,
}) {
  const { translate } = useTranslation();
  const [mode, setMode] = useState('new');
  const [skuLinkStrategy, setSkuLinkStrategy] = useState(SKU_LINK_CREATE_NEW);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productType, setProductType] = useState('PRODUCT');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [priceNumeric, setPriceNumeric] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [catalogAttributes, setCatalogAttributes] = useState([]);
  const [draftAttributes, setDraftAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [pendingNewOptions, setPendingNewOptions] = useState({});
  const [extraOptionsByAttrKey, setExtraOptionsByAttrKey] = useState({});
  const [existingSkus, setExistingSkus] = useState([]);
  const [selectedExistingSkuId, setSelectedExistingSkuId] = useState('');
  const [attrsLoading, setAttrsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [optionDefaultsOpen, setOptionDefaultsOpen] = useState(false);

  const mergedCatalog = useMemo(
    () => mergeCatalogWithDraftKeys(catalogAttributes, draftAttributes),
    [catalogAttributes, draftAttributes],
  );

  const skuRequiredAttrs = useMemo(() => {
    if (mode === 'existing' && catalogAttributes.length > 0) {
      return catalogAttributesRequiredForServerSku(catalogAttributes);
    }
    return catalogAttributesForSku(mergedCatalog);
  }, [mode, catalogAttributes, mergedCatalog]);

  const resolvePriceFromFormItem = useCallback((item) => {
    const numeric = Number(item?.unit_price) || 0;
    if (numeric > 0) return numeric;
    const display = String(item?.unit_priceDisplay ?? '').trim();
    if (!display) return 0;
    return parseFloat(parseCurrency(display)) || 0;
  }, []);

  const resetFormFromItem = useCallback(() => {
    const desc = String(formItem?.description ?? initialDescription ?? '').trim();
    const price = resolvePriceFromFormItem(formItem);
    const variationName = translate('products.default_variation_option_name');
    const defaultOptionValue = translate('products.default_variation_option_value');
    const { draft, attributeValues: defaultValues } = createDefaultVariationDraftFromItem(
      variationName,
      defaultOptionValue,
    );
    setMode('new');
    setSkuLinkStrategy(SKU_LINK_CREATE_NEW);
    setProductName(truncateProductName(desc));
    setProductDescription('');
    setProductType('PRODUCT');
    setPriceNumeric(price);
    setPriceDisplay(price > 0 ? formatCurrency(price) : '');
    setSelectedProduct(null);
    setCatalogAttributes([]);
    setDraftAttributes([draft]);
    setAttributeValues(defaultValues);
    setPendingNewOptions({});
    setExtraOptionsByAttrKey({});
    setExistingSkus([]);
    setSelectedExistingSkuId('');
    setLocalError('');
    setOptionDefaultsOpen(false);
  }, [formItem, initialDescription, translate, resolvePriceFromFormItem]);

  const registerDialogSessionKey = open
    ? `${formItem?.id ?? 'line'}-${String(initialDescription ?? '').trim()}`
    : '';

  useEffect(() => {
    if (!open) return;
    if (!formItem && !initialDescription) return;
    resetFormFromItem();
  }, [open, registerDialogSessionKey, resetFormFromItem, formItem, initialDescription]);

  useEffect(() => {
    if (!open || mode !== 'existing' || !selectedProduct?.id) {
      setCatalogAttributes([]);
      setExistingSkus([]);
      setSelectedExistingSkuId('');
      return;
    }
    let cancelled = false;
    setAttrsLoading(true);
    (async () => {
      try {
        const [attrs, skusRes] = await Promise.all([
          loadProductCatalogAttributes(selectedProduct.id),
          getSKUs(selectedProduct.id),
        ]);
        if (cancelled) return;
        setCatalogAttributes(attrs);
        setExistingSkus(skusRes.data || []);
        const initial = {};
        catalogAttributesForSku(attrs).forEach((attr) => {
          const key = attr.key || attr.attribute_key;
          if (!key) return;
          const opts = attributeOptionStrings(attr);
          if (opts.length === 1) initial[key] = opts[0];
        });
        setAttributeValues(initial);
      } catch (err) {
        if (!cancelled) setLocalError(err.message || String(err));
      } finally {
        if (!cancelled) setAttrsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, selectedProduct?.id]);

  useEffect(() => {
    if (!open || mode !== 'existing' || !selectedProduct?.id || attrsLoading) return;
    if (skuLinkStrategy === SKU_LINK_EXISTING) return;
    if (catalogAttributesForSku(catalogAttributes).length > 0) return;
    if (draftAttributes.length > 0) return;
    const variationName = translate('products.default_variation_option_name');
    const defaultOptionValue = translate('products.default_variation_option_value');
    const { draft, attributeValues: defaultValues } = createDefaultVariationDraftFromItem(
      variationName,
      defaultOptionValue,
    );
    setDraftAttributes([draft]);
    setAttributeValues(defaultValues);
  }, [
    open,
    mode,
    selectedProduct?.id,
    attrsLoading,
    skuLinkStrategy,
    catalogAttributes,
    draftAttributes.length,
    formItem,
    initialDescription,
    translate,
  ]);

  const draftsValid = draftAttributes.every((d) => {
    const name = String(d.name ?? '').trim();
    if (!name) return true;
    return (d.options || []).length > 0;
  });

  const filteredDraftAttributes = draftAttributes.filter((d) => String(d.name ?? '').trim());

  const namedDraftVariations = filteredDraftAttributes.filter(
    (d) => d.is_variation !== false && (d.options || []).length > 0,
  );

  const attributeValuesValid =
    skuLinkStrategy === SKU_LINK_EXISTING ||
    (skuRequiredAttrs.length > 0
      ? skuRequiredAttrs.every((attr) => {
          const key = attr.key || attr.attribute_key;
          if (!key) return true;
          const opts = attributeOptionStrings(attr);
          const extras = extraOptionsByAttrKey[key] || [];
          const val =
            String(attributeValues[key] ?? '').trim() ||
            String(pendingNewOptions[key] ?? '').trim() ||
            (extras.length > 0 ? String(extras[0]).trim() : '') ||
            (opts.length === 1 ? opts[0] : '');
          return !!val;
        })
      : namedDraftVariations.length > 0);

  const hasVariationSetup =
    skuLinkStrategy === SKU_LINK_EXISTING ||
    skuRequiredAttrs.length > 0 ||
    namedDraftVariations.length > 0;

  const canSubmitNew =
    productName.trim().length > 0 &&
    priceNumeric >= 0 &&
    draftsValid &&
    hasVariationSetup &&
    attributeValuesValid;

  const canSubmitExisting =
    selectedProduct?.id &&
    !attrsLoading &&
    draftsValid &&
    hasVariationSetup &&
    attributeValuesValid &&
    (skuLinkStrategy !== SKU_LINK_EXISTING || selectedExistingSkuId);

  const canSubmit = mode === 'new' ? canSubmitNew : canSubmitExisting;

  const shouldPromptOptionDefaults =
    mode === 'existing' &&
    skuLinkStrategy !== SKU_LINK_EXISTING &&
    draftAttributesNeedExistingDefaults(catalogAttributes, filteredDraftAttributes, existingSkus);

  const executeConversion = async (defaultValuesForExisting = {}) => {
    const descriptionFallback =
      mode === 'new' && productName.trim()
        ? productName.trim()
        : String(formItem?.description ?? initialDescription ?? '').trim();
    const savedItemId = await onEnsureItemSaved?.({ description: descriptionFallback || undefined });
    if (!savedItemId) {
      throw new Error(translate('orders.register_line_item_save_first'));
    }

    const formMetadata = formItem?.metadata || {};
    const common = {
      price: priceNumeric,
      formMetadata,
      draftAttributes: filteredDraftAttributes,
      attributeValues,
      pendingNewOptions,
      extraOptionsByAttrKey,
      skuLinkStrategy,
      existingSkuId: selectedExistingSkuId,
      defaultValuesForExisting,
    };

    let result;
    if (mode === 'new') {
      result = await registerLineItemAsNewProduct({
        name: productName.trim(),
        description: productDescription.trim(),
        type: productType,
        ...common,
      });
    } else {
      result = await registerLineItemOnExistingProduct({
        productId: selectedProduct.id,
        productCode: selectedProduct.code || selectedProduct.id,
        ...common,
      });
    }

    let mediaWarning = '';
    const formItemId = formItem?.id;
    if (formItemId && orderId) {
      try {
        await finalizeRegisterLineItemCatalogMedias({
          productId: result.productId,
          skuId: result.skuId,
          orderId,
          formItemId,
        });
      } catch (mediaErr) {
        mediaWarning =
          mediaErr.message || translate('orders.register_line_item_media_link_failed');
      }
    }

    await onConverted?.({
      skuId: result.skuId,
      productId: result.productId,
      productCode: result.productCode,
      reused: result.reused,
      created: result.created,
      mediaWarning,
    });
    onClose?.();
  };

  const handleConfirm = async () => {
    setLocalError('');
    if (shouldPromptOptionDefaults) {
      setOptionDefaultsOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      await executeConversion({});
    } catch (err) {
      let msg = err.message || String(err);
      if (/variation attributes are required/i.test(msg)) {
        msg = translate('orders.register_line_item_variation_required');
      }
      if (/valor padrão obrigatório/i.test(msg)) {
        msg = translate('products.option_defaults_required');
      }
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOptionDefaultsConfirm = async (defaults) => {
    setOptionDefaultsOpen(false);
    setSubmitting(true);
    setLocalError('');
    try {
      await executeConversion(defaults);
    } catch (err) {
      let msg = err.message || String(err);
      if (/variation attributes are required/i.test(msg)) {
        msg = translate('orders.register_line_item_variation_required');
      }
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const showAttributesSection =
    mode === 'new' ||
    (mode === 'existing' && selectedProduct?.id && skuLinkStrategy !== SKU_LINK_EXISTING);

  const lineItemSummary = String(formItem?.description ?? initialDescription ?? '').trim();
  const lineItemQty = Number(formItem?.quantity) || 1;

  return (
    <>
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {translate('orders.register_line_item_dialog_title')}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
          {translate('orders.register_line_item_dialog_subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {localError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {localError}
          </Alert>
        ) : null}

        {(lineItemSummary || lineItemQty > 0 || priceNumeric > 0) && (
          <Alert severity="info" icon={false} sx={{ mb: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
              {translate('orders.register_line_item_source')}
            </Typography>
            {lineItemSummary ? (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {lineItemSummary}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translate('orders.register_line_item_no_description')}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {translate('orders.register_line_item_source_meta')
                .replace('{qty}', String(lineItemQty))
                .replace('{price}', priceNumeric > 0 ? formatCurrency(priceNumeric) : '—')}
            </Typography>
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {translate('orders.register_line_item_mode_label')}
        </Typography>
        <RadioGroup
          row
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            value="new"
            control={<Radio size="small" disabled={submitting} />}
            label={translate('orders.register_line_item_mode_new')}
          />
          <FormControlLabel
            value="existing"
            control={<Radio size="small" disabled={submitting} />}
            label={translate('orders.register_line_item_mode_existing')}
          />
        </RadioGroup>

        <TextField
          fullWidth
          size="small"
          label={translate('orders.unit_price')}
          value={priceDisplay}
          disabled={submitting}
          onChange={(e) => {
            handleCurrencyInputChange(e, (synth) => {
              if (typeof synth === 'string') {
                setPriceNumeric(0);
                setPriceDisplay('');
              } else {
                setPriceNumeric(synth.numericValue);
                setPriceDisplay(synth.target.value);
              }
            });
          }}
          onBlur={() => {
            if (priceDisplay.trim()) {
              const n = parseFloat(parseCurrency(priceDisplay)) || 0;
              setPriceNumeric(n);
              if (n > 0) setPriceDisplay(formatCurrency(n));
            }
          }}
          sx={{ mb: 2 }}
        />

        {mode === 'new' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              required
              label={translate('orders.register_line_item_product_name')}
              value={productName}
              disabled={submitting}
              onChange={(e) => setProductName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              select
              label={translate('products.item_type')}
              value={productType}
              disabled={submitting}
              onChange={(e) => setProductType(e.target.value)}
            >
              {PRODUCT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {translate(t.labelKey) || t.value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label={translate('products.description')}
              value={productDescription}
              disabled={submitting}
              onChange={(e) => setProductDescription(e.target.value)}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <ProductSearchAutocomplete
              value={selectedProduct}
              onChange={(p) => {
                setSelectedProduct(p);
                setAttributeValues({});
                setPendingNewOptions({});
                setDraftAttributes([]);
              }}
              disabled={submitting}
              label={translate('orders.register_line_item_pick_product')}
            />
            {attrsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : null}
            {selectedProduct && !attrsLoading ? (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  {translate('orders.register_line_item_sku_strategy')}
                </Typography>
                <RadioGroup
                  value={skuLinkStrategy}
                  onChange={(e) => setSkuLinkStrategy(e.target.value)}
                >
                  <FormControlLabel
                    value={SKU_LINK_CREATE_NEW}
                    control={<Radio size="small" disabled={submitting} />}
                    label={translate('orders.register_line_item_sku_create_new')}
                  />
                  <FormControlLabel
                    value={SKU_LINK_REUSE_MATCH}
                    control={<Radio size="small" disabled={submitting} />}
                    label={translate('orders.register_line_item_sku_reuse_match')}
                  />
                  <FormControlLabel
                    value={SKU_LINK_EXISTING}
                    control={<Radio size="small" disabled={submitting} />}
                    label={translate('orders.register_line_item_sku_link_existing')}
                  />
                </RadioGroup>
                {skuLinkStrategy === SKU_LINK_EXISTING ? (
                  <FormControl fullWidth size="small" disabled={submitting}>
                    <InputLabel id="register-line-existing-sku">{translate('orders.sku')}</InputLabel>
                    <Select
                      labelId="register-line-existing-sku"
                      label={translate('orders.sku')}
                      value={selectedExistingSkuId}
                      onChange={(e) => setSelectedExistingSkuId(e.target.value)}
                    >
                      {existingSkus
                        .filter((s) => (s.status || 'ACTIVE') === 'ACTIVE')
                        .map((sku) => (
                          <MenuItem key={sku.id} value={sku.id}>
                            {sku.code || sku.id}
                            {sku.attributes && typeof sku.attributes === 'object'
                              ? ` — ${Object.entries(sku.attributes)
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(', ')}`
                              : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                ) : null}
              </>
            ) : null}
          </Box>
        )}

        {showAttributesSection ? (
          <>
            {mode === 'new' || (selectedProduct && skuLinkStrategy !== SKU_LINK_EXISTING) ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {translate('orders.register_line_item_variation_required_hint')}
              </Alert>
            ) : null}
            <LineItemCatalogAttributesSection
            catalogAttributes={catalogAttributes}
            draftAttributes={draftAttributes}
            onDraftAttributesChange={setDraftAttributes}
            attributeValues={attributeValues}
            onAttributeValuesChange={setAttributeValues}
            pendingNewOptions={pendingNewOptions}
            onPendingNewOptionsChange={setPendingNewOptions}
            extraOptionsByAttrKey={extraOptionsByAttrKey}
            onExtraOptionsByAttrKeyChange={setExtraOptionsByAttrKey}
            disabled={submitting || attrsLoading}
          />
          </>
        ) : null}

        {mode === 'new' && skuRequiredAttrs.length > 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {translate('orders.register_line_item_new_product_attrs_info')}
          </Alert>
        ) : null}

        <RegisterLineItemCatalogMediaSection
          open={open}
          orderId={orderId}
          order={order}
          formItems={formItems}
          formItem={formItem}
          disabled={submitting}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {translate('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {translate('orders.register_line_item_confirm')}
        </Button>
      </DialogActions>
    </Dialog>
    <OptionDefaultsForExistingDialog
      open={optionDefaultsOpen}
      newOptions={detectNewDraftAttributes(catalogAttributes, filteredDraftAttributes)}
      prefillDefaults={attributeValues}
      existingVariantCount={existingSkus.length}
      onClose={() => setOptionDefaultsOpen(false)}
      onConfirm={handleOptionDefaultsConfirm}
    />
    </>
  );
}
